import DashboardLayout from "@/components/DashboardLayout";
import { AllowancesTab } from "@/components/finance/AllowancesTab";
import { ManualExpensesTab } from "@/components/finance/ManualExpensesTab";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CalendarClock, Repeat } from "lucide-react";
import { SupplierInvoicesContent } from "@/pages/SupplierInvoices";
import { FinancePageHeader } from "@/components/finance/ui";
import { getExpensesTabFromSearch, getExpensesTabPath } from "@/finance/dashboardQueues";
import { useLocation, useSearch } from "wouter";

const pendingTabs = [
  {
    value: "recurring-expenses",
    title: "Gastos recurrentes",
    icon: Repeat,
    text: "Previsión recurrente frente a gasto real pendiente de modelo funcional.",
  },
] as const;

export default function ExpensesView() {
  const search = useSearch();
  const [, setLocation] = useLocation();
  const activeTab = getExpensesTabFromSearch(search);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <FinancePageHeader
          eyebrow="Finanzas y Contabilidad"
          title="Gastos"
          description="Facturas recibidas de proveedores, dietas, desplazamientos y gastos sin factura."
        />

        <Tabs
          value={activeTab}
          onValueChange={(value) => setLocation(getExpensesTabPath(search, value as typeof activeTab))}
          className="space-y-4"
        >
          <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 p-1 bg-muted/40 rounded-lg border border-border/60">
            <TabsTrigger value="supplier-invoices" className="text-xs font-medium px-3 py-1.5">
              Facturas recibidas
            </TabsTrigger>
            <TabsTrigger value="allowances" className="text-xs font-medium px-3 py-1.5">
              Dietas y tickets
            </TabsTrigger>
            <TabsTrigger value="manual-expenses" className="text-xs font-medium px-3 py-1.5">
              Otros gastos sin factura
            </TabsTrigger>
            <TabsTrigger value="recurring-expenses" className="text-xs font-medium px-3 py-1.5">
              Gastos recurrentes
            </TabsTrigger>
          </TabsList>

          <TabsContent value="supplier-invoices" className="focus-visible:outline-none">
            <SupplierInvoicesContent embedded />
          </TabsContent>

          <TabsContent value="allowances" className="focus-visible:outline-none">
            <AllowancesTab />
          </TabsContent>

          <TabsContent value="manual-expenses" className="focus-visible:outline-none">
            <ManualExpensesTab />
          </TabsContent>

          {pendingTabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <TabsContent key={tab.value} value={tab.value} className="focus-visible:outline-none">
                <Card className="border-dashed shadow-2xs">
                  <CardContent className="flex flex-col items-center justify-center gap-3 px-4 py-12 text-center">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="max-w-md space-y-1">
                      <p className="text-sm font-semibold text-foreground">{tab.title}</p>
                      <p className="text-xs text-muted-foreground leading-relaxed">{tab.text}</p>
                    </div>
                    <div className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-muted/30 px-3 py-1 text-xs font-medium text-muted-foreground">
                      <CalendarClock className="h-3.5 w-3.5" />
                      <span>En preparación</span>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            );
          })}
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
